<template>
  <div class="shell-page purchase-status-page">
    <HeaderMain @messageFromHeader="searchEvents" />
    <SearchCourse v-if="!main" :courses="search" />

    <section class="purchase-status-section">
      <div class="purchase-status-card purchase-status-card--hero">
        <div class="purchase-status-hero">
          <div class="purchase-status-hero__copy">
            <span class="section-heading__eyebrow">Pago en revisión</span>
            <h2 class="section-heading__title">{{ title }}</h2>
            <p class="section-heading__text">{{ description }}</p>
          </div>

          <div class="purchase-status-hero__actions">
            <router-link class="btn btn-primary purchase-status-button purchase-status-button--primary" :to="{ name: 'event', params: { slug: event.slug || id } }">
              <i class="fas fa-arrow-left"></i>
              Volver al evento
            </router-link>
            <button class="btn btn-outline-light purchase-status-button" type="button" @click="copyEventLink">
              <i class="fas fa-link"></i>
              Copiar link
            </button>
            <button class="btn btn-outline-light purchase-status-button purchase-status-button--share" type="button" @click="shareWhatsApp">
              <i class="fab fa-whatsapp"></i>
              Estado WA
            </button>
            <button class="btn btn-outline-light purchase-status-button purchase-status-button--share" type="button" @click="shareInstagram">
              <i class="fab fa-instagram"></i>
              Historia IG
            </button>
          </div>
        </div>

        <div class="purchase-status-steps">
          <article class="purchase-status-step is-done">
            <strong>01</strong>
            <span>Pago recibido</span>
            <small>La orden quedó registrada</small>
          </article>
          <article class="purchase-status-step is-active">
            <strong>02</strong>
            <span>Validación en curso</span>
            <small>Estamos revisando tu pago</small>
          </article>
          <article class="purchase-status-step">
            <strong>03</strong>
            <span>Envío pendiente</span>
            <small>Te avisamos por correo</small>
          </article>
        </div>

        <div class="purchase-status-highlights">
          <span>{{ category.name }}</span>
          <span>{{ venue.name }}{{ city ? ' · ' + city : '' }}</span>
          <span>{{ formatDate(event.start_date) }}</span>
          <span>Actualización por correo</span>
        </div>

        <div class="summary purchase-status-summary">
          <h3>{{ event.name }}</h3>
          <p>
            Seguimos validando tu compra. Apenas el sistema confirme el pago, recibirás tus accesos por correo y luego quedarán en <strong>Mis Tickets</strong>.
          </p>
          <p v-if="isStripePending && !stripeApproved" class="purchase-status-feedback">Estamos terminando de validar tu compra antes de emitir el ticket.</p>
          <p v-if="stripeApproved" class="purchase-status-feedback">Tu pago ya fue aprobado. Estamos dejando lista la descarga final.</p>
          <p v-if="copyMessage" class="purchase-status-feedback">{{ copyMessage }}</p>
        </div>

        <div v-if="isStripePending" class="purchase-status-loader" :class="{ 'is-approved': stripeApproved }">
          <div class="purchase-status-loader__pulse" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="purchase-status-loader__copy">
            <strong>{{ stripeApproved ? 'Pago aprobado. Estamos terminando tu ticket.' : 'Estamos preparando tu confirmación.' }}</strong>
            <p>{{ stripeApproved ? 'Apenas el archivo final quede listo te llevamos a la descarga.' : 'Estamos cerrando la compra y dejando lista la descarga de tus accesos.' }}</p>
          </div>

          <div class="purchase-status-loader__timeline">
            <span class="is-done">Pago</span>
            <span :class="stripeApproved ? 'is-done' : 'is-active'">Validación</span>
            <span :class="stripeApproved ? 'is-active' : ''">Ticket</span>
          </div>
        </div>
      </div>
    </section>

    <FooterMain />
  </div>
</template>

<script>
import { apiService } from '../common/api.services.js'
import HeaderMain from '@/components/HeaderMain.vue'
import FooterMain from '@/components/FooterMain.vue'
import SearchCourse from '@/components/SearchCourse.vue'

export default {
  name: 'PendingView',

  props: {
    id: {
      required: true,
    },
  },

  components: {
    HeaderMain,
    FooterMain,
    SearchCourse,
  },

  data() {
    return {
      search: [],
      searchField: '',
      main: true,
      event: {},
      venue: {},
      category: {},
      city: '',
      copyMessage: '',
      title: 'Entradas en validación',
      description: 'Estamos revisando tu pago para aprobar tus accesos con seguridad.',
      pollingHandle: null,
      stripeApproved: false,
    }
  },

  created() {
    this.getEvent()
    this.startPollingIfNeeded()
  },

  beforeUnmount() {
    this.stopPolling()
  },

  computed: {
    shareUrl() {
      if (!this.event || !this.event.slug) {
        return ''
      }

      return `${window.location.origin}/event/${this.event.slug}`
    },

    isStripePending() {
      return this.$route.query.provider === 'stripe'
    },
  },

  methods: {
    searchEvents(results, term) {
      this.search = results
      this.searchField = term
      this.main = term === ''
    },

    getEvent() {
      apiService(`/api/events/${this.id}/`).then((data) => {
        this.event = data
        this.category = data.category || {}
        this.venue = data.venue || {}
        this.city = data.venue && data.venue.city ? data.venue.city.name : ''

        if (data.access === 6) {
          this.title = 'Aplicación enviada'
          this.description = 'Tu aplicación fue enviada y el equipo la revisará en breve para aprobar tu ingreso.'
        }
      })
    },

    startPollingIfNeeded() {
      if (!this.isStripePending) {
        return
      }

      this.pollStripeStatus()
      this.pollingHandle = window.setInterval(() => {
        this.pollStripeStatus()
      }, 4000)
    },

    stopPolling() {
      if (this.pollingHandle) {
        window.clearInterval(this.pollingHandle)
        this.pollingHandle = null
      }
    },

    pollStripeStatus() {
      const attemptToken = this.$route.query.attempt
      if (!attemptToken) {
        return
      }

      const params = new URLSearchParams({ attempt_token: attemptToken })
      if (this.$route.query.payment_intent) {
        params.set('payment_intent_id', this.$route.query.payment_intent)
      }

      apiService(`/api/stripe/payment-status/?${params.toString()}`).then((data) => {
        if (!data || data.err) {
          return
        }

        if (data.status === 'approved' && data.invoice_id) {
          this.stopPolling()
          const confirmationQuery = {
            invoice: data.invoice_id || '',
            attempt: data.attempt_token || attemptToken,
            provider: 'stripe',
          }

          this.$router.replace({
            name: 'confirmation',
            params: { id: this.event.slug || this.id },
            query: confirmationQuery,
          })
          return
        }

        if (data.status === 'approved') {
          this.stripeApproved = true
          this.title = 'Pago aprobado'
          this.description = 'Tu cobro ya quedó aprobado. Ahora estamos terminando la emisión del ticket y el correo de confirmación.'
          return
        }

        if (data.status === 'failed') {
          this.stopPolling()
          this.title = 'Pago no aprobado'
          this.description = 'Stripe no pudo confirmar tu cobro. Puedes volver al evento e intentar con otro metodo.'
        }
      })
    },

    copyEventLink() {
      if (!this.shareUrl) {
        this.copyMessage = 'No encontramos el link público del evento todavía.'
        return
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.copyMessage = 'Link copiado.'
        })
        return
      }

      this.copyMessage = 'Tu navegador no permitió copiar automáticamente, pero el link sigue disponible aquí.'
    },

    shareWhatsApp() {
      if (!this.shareUrl) {
        this.copyMessage = 'No encontramos el link público del evento todavía.'
        return
      }

      this.shareToStatus('whatsapp')
    },

    shareInstagram() {
      if (!this.shareUrl) {
        this.copyMessage = 'No encontramos el link público del evento todavía.'
        return
      }

      this.shareToStatus('instagram')
    },

    shareToStatus(platform) {
      const networkLabel = platform === 'whatsapp' ? 'tu estado de WhatsApp' : 'tu historia de Instagram'
      const shareText = `Voy a ${this.event.name || 'este evento'} en FOMO`

      if (navigator.share) {
        navigator.share({
          title: this.event.name || 'FOMO',
          text: shareText,
          url: this.shareUrl,
        }).then(() => {
          this.copyMessage = `Se abrió el menú para compartir. Elige ${networkLabel} si tu dispositivo lo permite.`
        }).catch(() => {
          this.copyShareLink(networkLabel)
        })
        return
      }

      this.copyShareLink(networkLabel)
    },

    copyShareLink(networkLabel) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.copyMessage = `Link copiado. Ahora pégalo en ${networkLabel}.`
        })
        return
      }

      this.copyMessage = `Copia este link y pégalo en ${networkLabel}.`
    },

    formatDate(dateString) {
      return new Intl.DateTimeFormat('es-EC', {
        weekday: 'short',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date(dateString))
    },
  },
}
</script>

<style scoped>
.purchase-status-page {
  background:
    radial-gradient(circle at top right, rgba(123, 128, 156, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(10, 12, 18, 1), rgba(7, 9, 14, 1));
}

.purchase-status-section {
  width: min(100%, 1160px);
  margin: 0 auto;
  padding: 42px 20px 72px;
}

.purchase-status-card--hero {
  overflow: hidden;
  display: grid;
  gap: 24px;
  padding: 34px;
  border-radius: 34px;
  background:
    radial-gradient(circle at top right, rgba(120, 126, 151, 0.06), transparent 24%),
    linear-gradient(180deg, rgba(18, 20, 30, 0.98), rgba(10, 12, 18, 0.98));
  border: 1px solid rgba(198, 205, 220, 0.1);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.24);
}

.purchase-status-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
}

.purchase-status-hero__copy {
  max-width: 760px;
}

.purchase-status-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.purchase-status-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 20px;
  border-radius: 999px;
}

.purchase-status-button--primary {
  background: linear-gradient(135deg, #272c36, #414957) !important;
  border-color: transparent !important;
}

.purchase-status-button--share {
  min-width: 158px;
  justify-content: center;
}

.purchase-status-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.purchase-status-step {
  position: relative;
  display: grid;
  gap: 8px;
  padding: 16px 18px 16px 22px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(198, 205, 220, 0.08);
}

.purchase-status-step::before {
  content: '';
  position: absolute;
  inset: 14px auto 14px 0;
  width: 3px;
  border-radius: 999px;
  background: rgba(142, 147, 170, 0.3);
}

.purchase-status-step strong {
  color: rgba(187, 193, 209, 0.92);
  font-size: 0.74rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.purchase-status-step span {
  color: #eef2f7;
  font-size: 1.05rem;
}

.purchase-status-step small {
  color: rgba(186, 194, 208, 0.72);
}

.purchase-status-step.is-done,
.purchase-status-step.is-active {
  background: linear-gradient(180deg, rgba(22, 25, 36, 0.96), rgba(16, 18, 28, 0.96));
}

.purchase-status-step.is-done::before,
.purchase-status-step.is-active::before {
  background: linear-gradient(180deg, rgba(130, 136, 163, 0.82), rgba(103, 112, 141, 0.7));
}

.purchase-status-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.purchase-status-highlights span {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(198, 205, 220, 0.09);
  background: rgba(255, 255, 255, 0.03);
  color: #d9dfeb;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.purchase-status-summary {
  padding-top: 8px;
  border-top: 1px solid rgba(198, 205, 220, 0.08);
}

.purchase-status-summary h3 {
  color: #eef2f7;
}

.purchase-status-summary p {
  color: rgba(203, 210, 224, 0.82);
  line-height: 1.65;
}

.purchase-status-feedback {
  color: #d7deeb !important;
}

.purchase-status-loader {
  display: grid;
  gap: 16px;
  padding: 22px;
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(19, 23, 35, 0.94), rgba(12, 15, 24, 0.94));
  border: 1px solid rgba(198, 205, 220, 0.1);
}

.purchase-status-loader__pulse {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.purchase-status-loader__pulse span {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: rgba(236, 242, 247, 0.22);
  animation: statusPulse 1.2s ease-in-out infinite;
}

.purchase-status-loader__pulse span:nth-child(2) {
  animation-delay: 0.15s;
}

.purchase-status-loader__pulse span:nth-child(3) {
  animation-delay: 0.3s;
}

.purchase-status-loader__copy strong {
  color: #f7f9fc;
  font-size: 1.02rem;
}

.purchase-status-loader__copy p {
  margin: 8px 0 0;
  color: rgba(203, 210, 224, 0.78);
  line-height: 1.65;
}

.purchase-status-loader__timeline {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.purchase-status-loader__timeline span {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(198, 205, 220, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(221, 229, 243, 0.88);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.purchase-status-loader__timeline .is-done {
  background: rgba(236, 242, 247, 0.12);
  border-color: rgba(236, 242, 247, 0.2);
}

.purchase-status-loader__timeline .is-active {
  box-shadow: 0 0 0 1px rgba(236, 242, 247, 0.12) inset;
}

.purchase-status-loader.is-approved {
  background: linear-gradient(180deg, rgba(21, 26, 39, 0.96), rgba(13, 16, 25, 0.96));
}

@keyframes statusPulse {
  0%,
  100% {
    opacity: 0.25;
    transform: scale(0.9);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 900px) {
  .purchase-status-hero {
    display: grid;
  }

  .purchase-status-hero__actions {
    justify-content: flex-start;
  }

  .purchase-status-steps {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .purchase-status-section {
    padding: 24px 12px 56px;
  }

  .purchase-status-card--hero {
    padding: 22px 18px;
    border-radius: 26px;
  }

  .purchase-status-hero__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .purchase-status-button {
    justify-content: center;
    width: 100%;
    padding: 0 14px;
  }

  .purchase-status-button--primary {
    grid-column: 1 / -1;
  }

  .purchase-status-button--share {
    min-width: 0;
  }

  .purchase-status-highlights span {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
